import React from "react";

import { toast } from "react-toastify";

import { push } from "connected-react-router";

import * as ReportAPI from "api/ReportAPI";

import * as homeReducer from "containers/Home/reducer";
import * as foldersReducer from "containers/Folders/reducer";
import * as loginReducer from "containers/Login/reducer";
import * as galleryReducer from "containers/Gallery/GalleryReducer";
import * as nextReducer from "containers/Next/NextReducer";
import * as nextModelsReducer from "containers/Next/Models/ModelsReducer";
import * as globalReducer from "containers/App/reducer";
import * as projectReducer from "containers/Project/reducer";
import * as nextMonitoringReducer from "containers/Next/Monitoring/NextMonitoringReducer";
import * as deploymentsReducer from "containers/Deployments/reducer";
import * as paginationReducer from "containers/Pagination/reducer";
import * as viewReducer from "containers/View/reducer";
import * as chatDetailsReducer from "containers/ChatDetails/reducer";

export const REPORT_CHART_TYPES = {
  DISTRIBUTION: "distribution",
  OUTCOME: "outcome"
};

export const REPORT_CHART_SIZE = {
  SMALL: "small",
  MEDIUM: "medium",
  LARGE: "large",
  AUTO: "AUTO"
};

export const CHART_TYPES = {
  BAR: "bar",
  PIE: "pie",
  TREEMAP: "TREEMAP",
  FUNNEL: "funnel",
  LINE: "line",
  AREA: "area"
};

const UPDATE_BASE = "report/UPDATE_BASE";
const UPDATE_BASE_ADDITIVE = "report/UPDATE_BASE_ADDITIVE";
const UPDATE_FIELD = "report/UPDATE_FIELD";
const DELETE_FIELD = "report/DELETE_FIELD";
const UPDATE_FIELD_VALUE = "report/UPDATE_FIELD_VALUE";
const UPDATE_METADATA_FIELD_VALUE = "report/UPDATE_METADATA_FIELD_VALUE";
const UPDATE_CHART_VALUE = "report/UPDATE_CHART_VALUE";

const initialState = {};

function reportReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_BASE:
      return {
        ...action.data
      };
    case UPDATE_BASE_ADDITIVE:
      return {
        ...state,
        ...action.data
      };
    case UPDATE_FIELD:
      return {
        ...state,
        [action.id]: action.data
      };
    case DELETE_FIELD:
      const retState = { ...state };
      delete retState[action.id];
      return retState;
    case UPDATE_FIELD_VALUE:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          [action.key]: action.data
        }
      };
    case UPDATE_METADATA_FIELD_VALUE:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          metadata: {
            ...state[action.id].metadata,
            [action.key]: action.data
          }
        }
      };
    case UPDATE_CHART_VALUE:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          ["charts"]: {
            ...state[action.id]["charts"],
            [action.chartId]: {
              ...state[action.id]["charts"][action.chartId],
              [action.key]: action.data
            }
          }
        }
      };
    default:
      return state;
  }
}

/* Selectors */
export function getBase(state) {
  return state.report;
}

export function getReportsArr(state) {
  return Object.values(getBase(state));
}

export function getReport(state, id) {
  return getBase(state) ? getBase(state)[id] : undefined;
}

export function getExplorations(state, id) {
  return getReport(state, id)?.metadata?.explorations || {};
}

export function getExplorationHexMap(state, id, explorationId) {
  const explorations = getExplorations(state, id);

  return explorations?.[explorationId]?.hexMap || {};
}

export function getStopAnimation(state, id, explorationId) {
  const explorations = getExplorations(state, id);

  return explorations?.[explorationId]?.stopAnimation;
}

export function getReports(state) {
  return Object.keys(getBase(state))
    .filter((reportId) => reportId !== "new")
    .map((k) => getBase(state)[k])
    .filter((r) => r.isDeleted !== true);
}

export function getLinkReportDataset(state, id) {
  const report = getReport(state, id);
  const datasetId = report && report.metadata && report.metadata.linkedData && report.metadata.linkedData.datasetId;
  return datasetId && homeReducer.getDataset(state, datasetId);
}

export function getReportsFromDatasetId(state, datasetId) {
  const reportsArr = getReportsArr(state);
  return reportsArr.filter((report) => {
    let isLinked = false;
    const chartsArr = getChartsOrder(state, report.id);
    const charts = report && report.charts ? report.charts : {};

    chartsArr.forEach((chartId) => {
      if (charts[chartId] && charts[chartId].training_data_id === Number(datasetId)) {
        isLinked = true;
      }
    });
    return isLinked;
  });
}

export function getCharts(state, id) {
  return getReport(state, id) ? getReport(state, id).charts : undefined;
}

export function getChartsOrder(state, id, reportOverride) {
  const report = getReport(state, id);
  if (!report) {
    return [];
  }
  if (report.metadata && report.metadata.charts_order) {
    return report.metadata.charts_order;
  }
  const charts = getCharts(state, id);
  if (!charts) {
    return [];
  }
  return Object.keys(charts).filter((c) => !getChart(state, id, c).isDeleted);
}

export function getChart(state, reportId, chartId) {
  return getReport(state, reportId) && getReport(state, reportId).charts
    ? getReport(state, reportId).charts[chartId]
    : undefined;
}

export function fetchReport(id) {
  return (dispatch, getState) => {
    return ReportAPI.getReport(id).then((response) => {
      if (response) {
        const resNext = response?.metadata?.next
          ? response?.metadata?.next
          : response?.metadata?.explorations && Object.keys(response?.metadata?.explorations).length > 0
            ? (response?.metadata?.explorations[0] ?? {})
            : {};

        const resModels = { ...response?.metadata?.models };

        dispatch(nextReducer.updateNext(response.id, resNext));
        dispatch(nextModelsReducer.updateModels(response.id, resModels));
        dispatch(updateReportR(response.id, { ...response, id: response.id }));
        dispatch(nextReducer.initExploration(response.id));
      }
    });
  };
}

// This function expects only one report returned in the response array since there
// should only be one report associated with a given projectId.
export function fetchReportByProjectId(projectId) {
  return (dispatch, getState) => {
    const folderId = foldersReducer.getSelectedFolder(getState());
    return ReportAPI.getReportByProjectId(projectId, folderId).then((response) => {
      if (response) {
        const resNext = response?.[0]?.metadata?.next
          ? response?.[0]?.metadata?.next
          : response?.[0]?.metadata?.explorations && Object.keys(response?.[0]?.metadata?.explorations).length > 0
            ? (response?.[0]?.metadata?.explorations[0] ?? {})
            : {};

        const resModels = { ...response?.[0]?.metadata?.models };

        dispatch(nextReducer.updateNext(response?.[0].id, resNext));
        dispatch(nextModelsReducer.updateModels(response?.[0].id, resModels));
        dispatch(updateReportR(response?.[0].id, { ...response?.[0], id: response?.[0].id }));
        dispatch(nextReducer.initExploration(response?.[0].id));
        return response?.[0];
      }
    });
  };
}

// This function so we can find reports that are using a datasetId
export function fetchReportByDatasetId(datasetId) {
  return (dispatch, getState) => {
    const folderId = foldersReducer.getSelectedFolder(getState());
    return ReportAPI.getReportByDatasetId(datasetId, folderId).then((response) => {
      if (response) {
        const resNext = response?.[0]?.metadata?.next
          ? response?.[0]?.metadata?.next
          : response?.[0]?.metadata?.explorations && Object.keys(response?.[0]?.metadata?.explorations).length > 0
            ? (response?.[0]?.metadata?.explorations[0] ?? {})
            : {};

        const resModels = { ...response?.[0]?.metadata?.models };

        dispatch(nextReducer.updateNext(response?.[0].id, resNext));
        dispatch(nextModelsReducer.updateModels(response?.[0].id, resModels));
        dispatch(updateReportR(response?.[0].id, { ...response?.[0], id: response?.[0].id }));
        dispatch(nextReducer.initExploration(response?.[0].id));
        return response;
      }
    });
  };
}

export function fetchNextReports(isReloadAll, fetchChatDetails = false) {
  return (dispatch, getState) => {
    return dispatch(initFromStorage(false, isReloadAll, true, fetchChatDetails));
  };
}

export function fetchAllReports(isReloadAll = false) {
  return (dispatch, getState) => {
    return dispatch(initFromStorage(true, isReloadAll));
  };
}

export function initFromStorage(
  fetchAll = false,
  isReloadAll = false,
  fetchNext = false,
  fetchChatDetails = false,
  forceUpdateReports = false
) {
  return async (dispatch, getState) => {
    const state = getState();

    dispatch(globalReducer.updateIsReportsLoading(true));

    let latestId;
    if (fetchNext) {
      latestId = paginationReducer.getReportsPage(getState());
    }

    const folderId = foldersReducer.getSelectedFolder(state);
    return ReportAPI.getReports(folderId, fetchAll, latestId)
      .then(async (response) => {
        if (folderId !== foldersReducer.getSelectedFolder(getState())) {
          return;
        }

        dispatch(paginationReducer.storeLatestReport(response));

        const currentNextId = nextReducer.getCurrentNextId(getState());

        const currNext = nextReducer.getBase(getState());

        let nexts = {};
        let mNexts = {};
        let monitorNexts = {};

        Object.keys(currNext || {}).forEach((k) => {
          if (isNaN(k) || Number(k) === Number(currentNextId)) {
            nexts[k] = currNext[k];
          }
        });

        response
          ?.filter((next) => next?.id !== Number(currentNextId))
          .forEach((next) => {
            const baseNext = next?.metadata?.next
              ? next?.metadata?.next
              : next?.metadata?.explorations && Object.keys(next?.metadata?.explorations).length > 0
                ? (next?.metadata?.explorations[0] ?? {})
                : {};

            nexts[next.id] = { ...baseNext };
            mNexts[next.id] = { ...next?.metadata?.models };
            monitorNexts[next.id] = { ...next?.metadata?.monitoring };
          });

        dispatch(nextReducer.updateBase(nexts));
        dispatch(nextModelsReducer.updateBase(mNexts));
        dispatch(nextMonitoringReducer.updateBase(monitorNexts));

        const currReports = getBase(getState());
        let initReports = {};
        response?.forEach((report) => {
          // Skip reports that already have metadata downloaded
          const currReport = currReports[report.id];
          if (currReport?.metadata?.chatGPTConvo?.conversation !== undefined) {
            initReports[report.id] = { ...currReport, id: report.id };
          } else {
            initReports[report.id] = { ...report, id: report.id };
          }
        });

        if (fetchNext || forceUpdateReports) {
          dispatch(updateReports(initReports));
        } else {
          dispatch(updateBase(initReports));
        }

        response &&
          response
            .filter((report) => report.id !== Number(currentNextId))
            .forEach(async (report) => {
              if (report) {
                dispatch(nextReducer.initExploration(report.id));
              }
            });

        if (response && isReloadAll) {
          for (const relReport of response) {
            await dispatch(reloadReport(relReport.id));
          }
        }

        if (fetchChatDetails) {
          await dispatch(chatDetailsReducer.fetchAllChatMessageDetails());
        }

        return dispatch(globalReducer.updateIsReportsLoading(false));
      })
      .then(() => {
        return dispatch(deploymentsReducer.fetchAllDeployments());
      });
  };
}

export function saveToStorage(id) {
  return (dispatch, getState) => {
    const report = getReport(getState(), id);

    if (report && report.id) {
      ReportAPI.patchReport(report.id, report).then(
        (r) => r && r.metadata && dispatch({ type: UPDATE_FIELD, id: id, data: r })
      );
    }
  };
}

/* Actions */
export function updateBase(data) {
  return {
    type: UPDATE_BASE,
    data: data
  };
}

export function updateReports(data) {
  return {
    type: UPDATE_BASE_ADDITIVE,
    data: data
  };
}

export function updateReport(id, report) {
  return (dispatch, getState) => {
    dispatch({ type: UPDATE_FIELD, id: id, data: report });

    return dispatch(saveToStorage(id));
  };
}

export function updateReportR(id, report) {
  return (dispatch, getState) => {
    dispatch({ type: UPDATE_FIELD, id: id, data: report });
  };
}

export function updateChatGPTFieldValue(id, key, value) {
  return (dispatch, getState) => {
    const report = getReport(getState(), id);
    const reportChatGPT = report?.metadata?.chatGPTConvo ?? {};
    const newChatGPT = {
      ...reportChatGPT,
      [key]: value
    };
    return dispatch({
      type: UPDATE_METADATA_FIELD_VALUE,
      id: id,
      key: "chatGPTConvo",
      data: newChatGPT
    });
  };
}

export function updateIsSaving(id, isSaving) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: isSaving,
    key: "isSaving"
  };
}

export function updateChartsOrder(id, charts) {
  return {
    type: UPDATE_METADATA_FIELD_VALUE,
    id: id,
    data: charts,
    key: "charts_order"
  };
}

export function updateStopAnimation(id, explorationId) {
  return (dispatch, getState) => {
    const explorations = { ...getExplorations(getState(), id) };

    if (explorations?.[explorationId]) {
      explorations[explorationId].stopAnimation = true;

      dispatch(updateExplorations(id, explorations));
    }

    return Promise.resolve();
  };
}

export function updateExplorations(id, explorations) {
  return {
    type: UPDATE_METADATA_FIELD_VALUE,
    id: id,
    data: explorations,
    key: "explorations"
  };
}

export function addChartToReport(reportId, datasetId, chart) {
  return (dispatch, getState) => {
    let report = { ...getReport(getState(), reportId) };

    const chartId = Object.keys(report.charts || {}).length;

    const nextChart = {
      ...chart,
      id: chartId,
      title: chart.title
        ? chart.title
        : chart && chart.field && chart.field.label
          ? chart.field.label
          : "Distribution Chart",
      size: REPORT_CHART_SIZE.SMALL,
      datasetId: datasetId
    };

    const chartsOrder = report && report.metadata && report.metadata.charts_order ? report.metadata.charts_order : [];
    chartsOrder.push(String(chartId));
    report.charts_order = chartsOrder;

    toast(
      <div onClick={() => dispatch(push("/report/" + reportId))}>
        {"Chart added successfully. Click here to view report"}
      </div>,
      {
        type: toast.TYPE.SUCCESS,
        position: toast.POSITION.TOP_CENTER,
        autoClose: 7500
      }
    );

    ReportAPI.addChart(reportId, datasetId, nextChart).then((r) => {
      ReportAPI.patchReport(reportId, report).then((second) => {
        dispatch(reloadReport(reportId));
      });
    });
  };
}

export function cleanChartsOrder(id) {
  return (dispatch, getState) => {
    dispatch(updateChartsOrder(id, undefined));
    return dispatch(saveToStorage(id));
  };
}

export function deleteChart(reportId, chartId) {
  return (dispatch, getState) => {
    const rawReport = getReport(getState(), reportId);
    const report = { ...rawReport, metadata: { ...rawReport.metadata } };
    const chart = getChart(getState(), reportId, chartId);

    const charts = [...getChartsOrder(getState(), reportId)];
    const chartIndex = charts.findIndex((elem) => String(elem) === String(chartId));

    if (chartIndex >= 0) {
      charts.splice(chartIndex, 1);
    }

    const chartsOrder = charts.map((cId) => {
      const id = parseInt(cId);
      const deleteId = parseInt(chartId);
      return id > deleteId ? String(id - 1) : String(id);
    });

    report.metadata.charts_order = chartsOrder;

    return ReportAPI.patchReport(reportId, report).then((r) => {
      ReportAPI.deleteChart(chart.id).then((second) => {
        dispatch(reloadReport(reportId));
      });
    });
  };
}

export function updateChartDescription(reportId, chartId, description) {
  return (dispatch, getState) => {
    const chart = getChart(getState(), reportId, chartId);

    if (chart && chart.data) {
      const retChart = {
        ...chart,
        data: { ...chart.data, description: description }
      };

      ReportAPI.patchChart(chart.id, retChart).then((r) => {
        dispatch(reloadReport(reportId));
      });
    }

    return dispatch(saveToStorage(reportId));
  };
}

export function updateChartTitle(reportId, chartId, title) {
  return (dispatch, getState) => {
    const chart = getChart(getState(), reportId, chartId);

    if (chart && chart.data) {
      const retChart = {
        ...chart,
        data: { ...chart.data, title: title }
      };

      ReportAPI.patchChart(chart.id, retChart).then((r) => {
        dispatch(reloadReport(reportId));
      });
    }
  };
}

export function reloadReport(reportId) {
  return (dispatch, getState) => {
    return ReportAPI.getReport(reportId).then((r) => dispatch(updateReportR(reportId, r)));
  };
}

export function updateChartSize(reportId, chartId, size) {
  return (dispatch, getState) => {
    const chart = getChart(getState(), reportId, chartId);

    if (chart && chart.data) {
      const retChart = {
        ...chart,
        data: { ...chart.data, size: size }
      };

      ReportAPI.patchChart(chart.id, retChart).then((r) => {
        dispatch(reloadReport(reportId));
      });
    }

    return Promise.resolve();
  };
}

export function updateChartType(reportId, chartId, chartType) {
  return (dispatch, getState) => {
    const chart = getChart(getState(), reportId, chartId);

    if (chart && chart.data) {
      const retChart = {
        ...chart,
        data: { ...chart.data, chartType: chartType }
      };

      ReportAPI.patchChart(chart.id, retChart).then((r) => {
        dispatch(reloadReport(reportId));
      });
    }
  };
}

export function moveChart(reportId, chartId, direction) {
  return (dispatch, getState) => {
    const state = getState();
    const chartsOrder = [...getChartsOrder(state, reportId)] || [];
    const retChart = chartId;
    const chartLocation = chartsOrder.findIndex((elem) => String(elem) === String(chartId));
    let newIndex = Math.min(chartsOrder.length, Math.max(0, chartLocation + direction));

    chartsOrder.splice(chartLocation, 1);
    chartsOrder.splice(newIndex, 0, retChart);

    const report = {
      ...getReport(getState(), reportId),
      metadata: {
        ...getReport(getState(), reportId).metadata,
        charts_order: chartsOrder
      }
    };

    dispatch(updateReportR(reportId, report));

    ReportAPI.patchReport(reportId, report).then((second) => {
      dispatch(reloadReport(reportId));
    });
  };
}

export function saveReport(reportId) {
  return async (dispatch, getState) => {
    let report = getReport(getState(), reportId);
    try {
      report = await ReportAPI.loadCustomerMetadata(report);
    } catch (e) {
      console.log(e);
    }
    const retReport = await ReportAPI.patchReport(reportId, report);
    dispatch(updateReport(reportId, retReport));
    return Promise.resolve();
  };
}

export function localDeleteReport(reportId) {
  return { type: DELETE_FIELD, id: reportId };
}

export function deleteReport(reportId) {
  return (dispatch, getState) => {
    const projectId = nextReducer.getProjectId(getState(), reportId);
    const chatApps = viewReducer.getChatApps(getState());
    const linkedChatApps = chatApps?.filter((app) => app?.wizard?.savedNextId == reportId);

    let deleteConfirmText =
      "Are you sure you want to delete this exploration" + (projectId !== undefined ? " and models?" : "?");

    if (linkedChatApps?.length === 1) {
      deleteConfirmText =
        deleteConfirmText + " 1 chat app is currently using pinned charts from the exploration in this project.";
    }

    if (linkedChatApps?.length > 1) {
      deleteConfirmText =
        deleteConfirmText +
        ` ${linkedChatApps?.length} chat apps are currently using pinned charts from the exploration in this project.`;
    }

    const shouldDelete = confirm(deleteConfirmText);

    if (shouldDelete) {
      ReportAPI.deleteReport(reportId).then((r) => {
        return (
          r &&
          dispatch({
            type: DELETE_FIELD,
            id: r.id
          })
        );
      });

      if (projectId !== undefined) {
        dispatch(projectReducer.handleDelete(projectId, true));
      }
    }

    return Promise.resolve();
  };
}

export function createChartJson(field, type, outcome, success, datasetId) {
  return {
    field: field,
    type: type,
    outcome: outcome,
    outcomeSuccess: success,
    datasetId: datasetId,
    isScaled: false,
    title: type === REPORT_CHART_TYPES.OUTCOME ? field.label + " by " + outcome : field.label + " Distribution",
    showAxis: true,
    showTitle: true
  };
}

export function saveExploration(reportId) {
  return (dispatch, getState) => {
    const explorationKey = nextReducer.getExplorationKey(getState(), reportId);

    let explorations = {
      ...getExplorations(getState(), reportId),
      [explorationKey]: {
        ...nextReducer.getNext(getState(), reportId),
        stopAnimation: undefined
      }
    };

    const report = getReport(getState(), reportId);

    if (report === undefined) {
      dispatch(updateReport(reportId, { metadata: {} }));
    }

    return dispatch(updateExplorations(reportId, explorations));
  };
}

export function createLinkedReport(report) {
  return (dispatch, getState) => {
    if (report && report.datasetId) {
      const dataset = homeReducer.getDataset(getState(), report.datasetId);

      dispatch(galleryReducer.updateIsCreatingLinkedReport(report.datasetId, true));

      const retReport = {
        label: report.name,
        created: new Date(),
        description: report.description,
        created_by: loginReducer.getUserName(getState()),
        charts_order: [],
        linkedData: { ...report }
      };

      const projectFolderId = globalReducer.getProjectFolderId(getState());
      const selectedFolderId = foldersReducer.getSelectedFolder(getState());

      if (projectFolderId !== undefined && projectFolderId !== selectedFolderId) {
        dispatch(foldersReducer.selectFolder(projectFolderId));
      }

      const folderId = projectFolderId !== undefined ? projectFolderId : selectedFolderId;

      ReportAPI.createReport(folderId, retReport).then((response) => {
        const reportId = response.id;

        dispatch({
          type: UPDATE_FIELD,
          id: reportId,
          data: { ...response }
        });

        if (
          report.yColumns === undefined ||
          report.xColumns === undefined ||
          report.yColumns.length === 0 ||
          report.xColumns.length === 0
        ) {
          toast(
            <div onClick={() => dispatch(push("/report/" + reportId))}>
              {"Report: " + report.name + " created successfully. Click here to view report"}
            </div>,
            {
              type: toast.TYPE.SUCCESS,
              position: toast.POSITION.TOP_CENTER,
              autoClose: 7500,
              onClick: (e) => {
                dispatch(push("/reports/" + reportId));
              }
            }
          );

          return dispatch(galleryReducer.updateIsCreatingLinkedReport(report.datasetId, false));
        } else {
          let addChartPromises = [];
          let chartOrder = [];

          let chartCount = 0;

          const promises = [
            ...report.yColumns
              .filter(
                (yField) =>
                  dataset &&
                  dataset.fields &&
                  dataset.fields[yField.label] &&
                  (dataset.fields[yField.label].inclusionProbabilities ||
                    dataset.fields[yField.label].inclusionProbabilitiesWarning)
              )
              .flatMap((yField) => [
                ReportAPI.addChart(
                  reportId,
                  report.datasetId,
                  createChartJson(
                    dataset.fields[yField.label],
                    REPORT_CHART_TYPES.DISTRIBUTION,
                    undefined,
                    undefined,
                    report.datasetId
                  )
                ),
                ...report.xColumns.map((xField, i) => {
                  if (i === 0) {
                    chartOrder.push(chartCount + "");
                    chartCount++;
                  }

                  chartOrder.push(chartCount + "");
                  chartCount++;

                  const x = dataset.fields[xField.label];
                  const y = dataset.fields[yField.label];

                  const retChart = createChartJson(
                    x,
                    REPORT_CHART_TYPES.OUTCOME,
                    yField.label,
                    yField.success,
                    report.datasetId
                  );

                  return ReportAPI.addChart(reportId, report.datasetId, retChart);
                })
              ])
              .filter((p, i) => i < 10),
            ReportAPI.patchReport(reportId, {
              ...response,
              metadata: {
                ...response.metadata,
                charts_order: chartOrder
              }
            })
          ];

          Promise.all(promises).then((r) => {
            toast(
              <div onClick={() => dispatch(push("/report/" + reportId))}>
                {"Report: " + report.name + " created successfully. Click here to view report"}
              </div>,
              {
                type: toast.TYPE.SUCCESS,
                position: toast.POSITION.TOP_CENTER,
                autoClose: 7500,
                onClick: (e) => {
                  dispatch(push("/reports/" + reportId));
                }
              }
            );

            dispatch(reloadReport(reportId));

            return dispatch(galleryReducer.updateIsCreatingLinkedReport(report.datasetId, false));
          });
        }
      });
    }

    return Promise.resolve();
  };
}

export function createReport(label, datasetId, chart) {
  return (dispatch, getState) => {
    const reportCount = Object.keys(getBase(getState())).length;

    const report = {
      label: label,
      created: new Date(),
      created_by: loginReducer.getUserName(getState()),
      charts_order: ["0"],
      charts: {
        0: {
          ...chart,
          id: 0,
          title: chart.title
            ? chart.title
            : chart && chart.field && chart.field.label
              ? chart.field.label
              : "Distribution Chart",
          size: REPORT_CHART_SIZE.SMALL,
          datasetId: datasetId
        }
      }
    };

    const projectFolderId = globalReducer.getProjectFolderId(getState());
    const selectedFolderId = foldersReducer.getSelectedFolder(getState());

    if (projectFolderId !== undefined && projectFolderId !== selectedFolderId) {
      dispatch(foldersReducer.selectFolder(projectFolderId));
    }

    const folderId = projectFolderId !== undefined ? projectFolderId : selectedFolderId;

    ReportAPI.createReport(folderId, report).then((response) => {
      const reportId = response.id;

      dispatch({
        type: UPDATE_FIELD,
        id: reportId,
        data: { ...response }
      });

      ReportAPI.addChart(reportId, datasetId, report.charts[0]).then((r) => dispatch(reloadReport(reportId)));

      toast(
        <div onClick={() => dispatch(push("/report/" + reportId))}>
          {"Report: " + label + " created successfully. Click here to view report"}
        </div>,
        {
          type: toast.TYPE.SUCCESS,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 7500,
          onClick: (e) => {
            dispatch(push("/reports/" + reportId));
          }
        }
      );

      return dispatch(
        galleryReducer.updateAddChartId(datasetId, {
          label: label,
          value: reportId
        })
      );
    });

    return Promise.resolve();
  };
}

export function init(id) {
  return (dispatch, getState) => {
    const sFolderId = foldersReducer.getSelectedFolder(getState());
    const report = getReport(getState(), id);

    if (!report) {
      return dispatch(reloadReport(id)).then((r) => {
        const nReport = getReport(getState(), id);
        if (nReport.folder_id !== sFolderId) {
          return dispatch(foldersReducer.updateSelectedFolder(nReport.folder_id));
        }
      });
    } else {
      if (report.folder_id !== sFolderId) {
        return dispatch(foldersReducer.updateSelectedFolder(report.folder_id));
      }
    }
  };
}

export default reportReducer;
